import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { Fatura } from '@/features/faturas/types';

import {
  DEFAULT_ESTORNOS_FILTERS,
  DEFAULT_PAINEL_FILTERS,
  type AntecipacaoPagamentoResultado,
  type ConsumoEmAberto,
  type Estorno,
  type EstornosFilters,
  type FinanceiroEscritorioState,
  type PainelFinanceiroFilters,
  type PainelFinanceiroResumo,
} from '../types';
import {
  fetchConsumoEmAberto,
  fetchEstornos,
  fetchFaturaEscritorioById,
  fetchFaturasEscritorio,
  fetchPainelResumo,
  gerarFaturaAntecipada,
} from './actions';

const initialState: FinanceiroEscritorioState = {
  resumo: null,
  faturas: [],
  faturaAtual: null,
  filters: DEFAULT_PAINEL_FILTERS,
  consumoEmAberto: [],
  consumoEmAbertoLoading: false,
  estornos: [],
  estornosLoading: false,
  estornosFilters: DEFAULT_ESTORNOS_FILTERS,
  loading: false,
  loadingDetalhe: false,
  saving: false,
  error: null,
  bannerDispensadoNaSessao: false,
};

const upsertFatura = (state: FinanceiroEscritorioState, fatura: Fatura) => {
  const idx = state.faturas.findIndex((f) => f.id === fatura.id);
  if (idx >= 0) {
    state.faturas[idx] = fatura;
  } else {
    state.faturas.unshift(fatura);
  }
  if (state.faturaAtual?.id === fatura.id) {
    state.faturaAtual = fatura;
  }
};

const financeiroEscritorioSlice = createSlice({
  name: 'financeiroEscritorio',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<Partial<PainelFinanceiroFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = DEFAULT_PAINEL_FILTERS;
    },
    dispensarBanner: (state) => {
      state.bannerDispensadoNaSessao = true;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearFaturaAtual: (state) => {
      state.faturaAtual = null;
    },
    setEstornosFilters: (state, action: PayloadAction<Partial<EstornosFilters>>) => {
      state.estornosFilters = { ...state.estornosFilters, ...action.payload };
    },
    clearEstornosFilters: (state) => {
      state.estornosFilters = DEFAULT_ESTORNOS_FILTERS;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPainelResumo.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchPainelResumo.fulfilled,
        (state, action: PayloadAction<PainelFinanceiroResumo>) => {
          state.loading = false;
          state.resumo = action.payload;
        },
      )
      .addCase(fetchPainelResumo.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) ?? 'Erro ao carregar painel financeiro';
      })

      .addCase(fetchFaturasEscritorio.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFaturasEscritorio.fulfilled, (state, action: PayloadAction<Fatura[]>) => {
        state.loading = false;
        state.faturas = action.payload;
      })
      .addCase(fetchFaturasEscritorio.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) ?? 'Erro ao carregar faturas';
      })

      .addCase(fetchFaturaEscritorioById.pending, (state) => {
        state.loadingDetalhe = true;
        state.error = null;
      })
      .addCase(fetchFaturaEscritorioById.fulfilled, (state, action: PayloadAction<Fatura>) => {
        state.loadingDetalhe = false;
        state.faturaAtual = action.payload;
        upsertFatura(state, action.payload);
      })
      .addCase(fetchFaturaEscritorioById.rejected, (state, action) => {
        state.loadingDetalhe = false;
        state.faturaAtual = null;
        state.error = (action.payload as string) ?? 'Fatura não encontrada';
      })

      .addCase(fetchConsumoEmAberto.pending, (state) => {
        state.consumoEmAbertoLoading = true;
        state.error = null;
      })
      .addCase(
        fetchConsumoEmAberto.fulfilled,
        (state, action: PayloadAction<ConsumoEmAberto[]>) => {
          state.consumoEmAbertoLoading = false;
          state.consumoEmAberto = action.payload;
        },
      )
      .addCase(fetchConsumoEmAberto.rejected, (state, action) => {
        state.consumoEmAbertoLoading = false;
        state.error = (action.payload as string) ?? 'Erro ao carregar itens em aberto';
      })

      .addCase(gerarFaturaAntecipada.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(
        gerarFaturaAntecipada.fulfilled,
        (state, action: PayloadAction<AntecipacaoPagamentoResultado>) => {
          state.saving = false;
          action.payload.faturasGeradas.forEach((fatura) => upsertFatura(state, fatura));
          const faturados = new Set(action.payload.itensFaturados);
          state.consumoEmAberto = state.consumoEmAberto.filter(
            (item) => !(item.eventIds.length > 0 && item.eventIds.every((id) => faturados.has(id))),
          );
        },
      )
      .addCase(gerarFaturaAntecipada.rejected, (state, action) => {
        state.saving = false;
        state.error = (action.payload as string) ?? 'Erro ao gerar fatura antecipada';
      })

      .addCase(fetchEstornos.pending, (state) => {
        state.estornosLoading = true;
        state.error = null;
      })
      .addCase(fetchEstornos.fulfilled, (state, action: PayloadAction<Estorno[]>) => {
        state.estornosLoading = false;
        state.estornos = action.payload;
      })
      .addCase(fetchEstornos.rejected, (state, action) => {
        state.estornosLoading = false;
        state.error = (action.payload as string) ?? 'Erro ao carregar estornos';
      });
  },
});

export const {
  setFilters,
  clearFilters,
  dispensarBanner,
  clearError,
  clearFaturaAtual,
  setEstornosFilters,
  clearEstornosFilters,
} = financeiroEscritorioSlice.actions;
export const financeiroEscritorioReducer = financeiroEscritorioSlice.reducer;
